import * as React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import professor from '~/images/prof.png';
import Modal from '~/theseus/components/Modal';
import Button from '~/theseus/components/Button';
import { setupFbPixel } from '~/api/routes/auth/user';

import { hasPageAccess } from '~/theseus/hooks/useAuthStatus';

import {
  useAccountQuery,
  useAccountsQuery,
  usePageQuery,
  usePagesQuery,
} from '~/theseus/hooks/queries';

import useAuthModalState from './useAuthModalState';
import { useAuthSelector } from '~/theseus/containers/Auth';

const Cross = () => (
  <FontAwesomeIcon
    icon={faTimes}
    color="tomato"
    css={css`
      scale: 1.2;
      margin-right: 10px;
    `}
  />
);

const ModalContent = styled.div`
  & * {
    font: revert;
  }

  line-height: 30px;
  margin: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;

  h1 {
    text-align: center;
    margin: 0 auto 20px 0;
  }

  ul {
    ul {
      margin-left: 30px;
      list-style-type: circle;
    }
  }

  img {
    width: 150px;
  }
`;

function ChecklistItem(props) {
  const { label, valid, children } = props;

  if (valid) {
    return null;
  }
  return (
    <li>
      <Cross />
      <strong>
        {label}
      </strong>
      :
      <ul>
        <li>{children}</li>
      </ul>
    </li>
  );
}

function AuthModal() {
  const {
    isOpen,
    isOwner,
    appRequirements,
    openFbRerequestModal,
  } = useAuthModalState();

  const [accountId, setAccountId] = useAccountQuery();
  const [pageId, setPageId] = usePageQuery();

  const { data: accounts } = useAccountsQuery();
  const { data: pages } = usePagesQuery();

  const displayName = useAuthSelector(state => `${state.context.rgUser?.rg_info?.first_name} ${state.context.rgUser?.rg_info?.last_name}`);
  const currentAccount = React.useMemo(() => accounts?.find(({ id }) => id === accountId), [accounts, accountId]);
  const currentPage = React.useMemo(() => pages?.find(({ id }) => id === pageId), [pages, pageId]);

  const changeCurrentAccount = React.useCallback((e) => {
    setAccountId(e.target.value);
  }, [setAccountId]);

  const changeCurrentPage = React.useCallback((e) => {
    setPageId(e.target.value);
  }, [setPageId]);

  const reloadPage = React.useCallback(() => {
    window.location.reload(false);
  }, []);

  const queryClient = useQueryClient();
  const pixelSetup = useMutation(() => setupFbPixel(accountId as string), {
    onSuccess: () => {
      queryClient.invalidateQueries(['facebook-pixel']);
    },
  });

  return (
    <Modal isOpen={isOpen} withClose={false} onClose={() => {}} contentStyle={{ maxWidth: 800, maxHeight: '85vh' }}>
      <ModalContent>
        <img src={professor} alt="Real Geeks professor logo" />
        <h1>Welcome to the Realgeeks Facebook Ad Tool!</h1>
        <p>
          <strong>
            You are missing a few things needed to use the Facebook tool:
          </strong>
        </p>
        <ul>
          <ChecklistItem
            label="Facebook page"
            valid={appRequirements.pages.valid}
            key="have-pages"
          >
            Make sure that you have a page and link it to your business manager.
            Click&nbsp;
            <a
              href="https://facebook.com/pages/create"
              target="_blank"
              rel="noreferrer noopen"
            >
              here
            </a>
            &nbsp;to create a Facebook page
          </ChecklistItem>
          <ChecklistItem
            label="Facebook ad account"
            valid={appRequirements.accounts.valid}
            key="have-accounts"
          >
            To run ads, you will need a Facebook ad account. Click&nbsp;
            <a
              href="https://business.facebook.com"
              target="_blank"
              rel="noreferrer noopen"
            >
              here
            </a>
            &nbsp;to create one.
          </ChecklistItem>
          <ChecklistItem
            label="Facebook Page Access"
            valid={appRequirements.hasPageAccess}
            key="have-page-access"
          >
            You need to have &quot;Publish Content&quot; and &quot;Create Ads&quot; access enabled for&nbsp;
            <strong>{currentPage?.name}</strong>
            . To do this, click&nbsp;
            <a href={`https://business.facebook.com/settings/pages/${pageId}`} target="_blank" rel="noreferrer noopen">here</a>
            &nbsp;and enable the correct permissions&nbsp;
            <strong>or</strong>
            &nbsp;switch to a page that you have access to:
            <br />
            <select onChange={changeCurrentPage} value={currentPage?.id}>
              {pages?.map(page => <option value={page.id} key={page.id} disabled={!hasPageAccess(page)}>{page.name}</option>)}
            </select>

          </ChecklistItem>
          <ChecklistItem
            label="Site owner authorization"
            valid={appRequirements.isAuthorized}
            key="authorized"
          >
            You are not authorized to create ads for this site using Facebook.
            Please ask the site owner to make you an admin for &apos;facebook&apos; in
            accounts.realgeeks.com.&nbsp;
            <a
              href="https://support.realgeeks.com/hc/en-us/articles/1500002203041"
              target="_blank"
              rel="noreferrer noopen"
            >
              Click here to learn more
            </a>
          </ChecklistItem>
          <ChecklistItem
            label="Facebook permissions"
            valid={appRequirements.permissions.valid}
            key="fb-permissions"
          >
            Make sure that you have enabled all of the permissions we ask for
            when logging in on Facebook so that are able to post on your behalf
            to your accounts and retrieve your ad information. You can
            re-request your permissions by clicking&nbsp;
            <Button type="button" onClick={openFbRerequestModal}>here</Button>
          </ChecklistItem>
          <ChecklistItem
            label="Enable site"
            valid={appRequirements.siteEnabled}
            key="site-enabled"
          >
            {isOwner ? (
              <React.Fragment>
                You can enable your site&nbsp;
                <a href="/enable.html">here</a>
              </React.Fragment>
            ) : (
              'Please ask the site owner to enable this site before proceeding with the tool'
            )}
          </ChecklistItem>
          <ChecklistItem
            label="Facebook pixel"
            valid={appRequirements.fbPixel.valid}
            key="fb-pixel"
          >
            Your current account&nbsp;
            <strong>
              { currentAccount?.name }
            </strong>
            &nbsp;needs a Facebook pixel that is also connected to your site.
            We can set that up for you:&nbsp;
            <Button
              type="button"
              onClick={() => pixelSetup.mutate()}
              disabled={pixelSetup.isLoading || !accountId}
            >
              {pixelSetup.isLoading ? 'Setting up…' : 'Set up my pixel'}
            </Button>
            {pixelSetup.isError && (
              <p role="alert" css={css`color: red;`}>
                We couldn&apos;t set up the pixel automatically. You can set one up
                manually by following&nbsp;
                <a
                  href="https://support.realgeeks.com/hc/en-us/articles/360054033414-Adding-a-Facebook-Pixel-to-a-Real-Geeks-Website"
                  target="_blank"
                  rel="noreferrer noopen"
                >
                  this guide
                </a>
              </p>
            )}
          </ChecklistItem>
          <ChecklistItem
            label="Disable ad blocker"
            valid={appRequirements.adBlockerDisabled}
            key="ad-blocker"
          >
            Using an ad blocker can cause some issues when using the Ads Manager. Please disable your ad blocker and reload the page.
          </ChecklistItem>
          {
            accounts
              && (
              <ChecklistItem
                label="Payment method"
                valid={appRequirements.fundingSource.valid}
                key="funding-source"
              >
                You must have a payment method attached to your account in order to run ads. Please add a funding source to your account or change your selected account. You can add a funding source&nbsp;
                <a
                  href="https://www.facebook.com/ads/manager/account_settings/account_billing"
                  target="_blank"
                  rel="noreferrer noopen"
                >
                  here
                </a>
                &nbsp;
                <strong>or</strong>
                &nbsp;switch to an account that has a funding source here:&nbsp;
                <select onChange={changeCurrentAccount} value={currentAccount?.id}>
                  {accounts?.map(account => <option value={account.id} key={account.id}>{account.name}</option>)}
                </select>

              </ChecklistItem>
              )
          }
        </ul>
        <p>
          <strong>
            Once you have fixed all of the above items, reload the page to begin
            creating ads on Facebook
          </strong>
        </p>
        <Button size="lg" onClick={reloadPage}>
          Reload
        </Button>
      </ModalContent>
    </Modal>
  );
}

export default AuthModal;
